/**
 * Predicate builders for TypeGraph queries.
 *
 * Provides a fluent API for building type-safe predicates.
 */
import { type FulltextQueryMode } from "../backend/types";
import { type NullCheckOp } from "../core/types";
import { ConfigurationError, UnsupportedPredicateError } from "../errors";
import {
  type ArrayOp,
  type ArrayPredicate,
  type BetweenPredicate,
  type ComparisonOp,
  type ComparisonPredicate,
  type ExistsSubquery,
  type FieldRef,
  type FulltextMatchPredicate,
  type InSubquery,
  type LiteralValue,
  type NullPredicate,
  type ObjectOp,
  type ObjectPredicate,
  type ParameterRef,
  type PredicateExpression,
  type QueryAst,
  type StringOp,
  type StringPredicate,
  type ValueType,
  type VectorMetricType,
  type VectorSimilarityPredicate,
} from "./ast";
import { CURRENT_READ_INSTANT_PLACEHOLDER } from "./compiler/temporal";
import { resolveFieldTypeInfoAtJsonPointer } from "./field-type-info";
import {
  joinJsonPointers,
  type JsonPointer,
  jsonPointer,
  type JsonPointerInput,
  type JsonPointerSegment,
  normalizeJsonPointer,
  type ResolveJsonPointer,
  type ResolveJsonPointerSegments,
} from "./json-pointer";
import { type FieldTypeInfo } from "./schema-introspector";
import {
  getSingleSubqueryColumnValueType,
  getSubqueryColumnCount,
  isInSubqueryTypeCompatible,
  isUnsupportedInSubqueryValueType,
} from "./subquery-utils";

// ============================================================
// Predicate Builder
// ============================================================

/**
 * A chainable predicate that can be combined with AND/OR.
 */
export type Predicate = Readonly<{
  __expr: PredicateExpression;
  and: (other: Predicate) => Predicate;
  or: (other: Predicate) => Predicate;
  not: () => Predicate;
}>;

/**
 * Creates a predicate wrapper with chainable methods.
 */
function predicate(expr: PredicateExpression): Predicate {
  return {
    __expr: expr,
    and: (other: Predicate): Predicate =>
      predicate({
        __type: "and",
        predicates: [expr, other.__expr],
      }),
    or: (other: Predicate): Predicate =>
      predicate({
        __type: "or",
        predicates: [expr, other.__expr],
      }),
    not: (): Predicate =>
      predicate({
        __type: "not",
        predicate: expr,
      }),
  };
}

// ============================================================
// Parameter References
// ============================================================

/**
 * Creates a named parameter reference for prepared queries.
 *
 * Use with `query.prepare()` to create reusable parameterized queries.
 * Supported in scalar comparison positions (eq, neq, gt, etc.), string
 * operations, between bounds, and as the WHOLE list of `in()`/`notIn()` —
 * `field.in(param("ids"))` bound to an array at execute time. A `param()`
 * among the individual elements of a literal list is rejected.
 *
 * @example
 * ```typescript
 * const prepared = store.query()
 *   .from("Person", "p")
 *   .whereNode("p", (p) => p.name.eq(param("name")))
 *   .select((ctx) => ctx.p)
 *   .prepare();
 *
 * const results = await prepared.execute({ name: "Alice" });
 * ```
 *
 * @example List-valued parameter — one compiled statement, any list length.
 * ```typescript
 * const byIds = store.query()
 *   .from("Person", "p")
 *   .whereNode("p", (p) => p.id.in(param("ids")))
 *   .select((ctx) => ctx.p)
 *   .prepare();
 *
 * await byIds.execute({ ids: ["a", "b"] });
 * await byIds.execute({ ids: ["c"] });
 * ```
 */
// eslint-disable-next-line unicorn/name-replacements -- concise public API
export function param(name: string): ParameterRef {
  // The compiled template fills this reserved placeholder with the current read
  // instant, so a user param of the same name would be silently overwritten by
  // the wall clock (see fillTemplateParams). Reject it up front.
  if (name === CURRENT_READ_INSTANT_PLACEHOLDER) {
    throw new ConfigurationError(
      `Parameter name "${name}" is reserved for internal use`,
      { parameterName: name },
    );
  }
  return { __type: "parameter", name };
}

/**
 * Type guard for ParameterRef values.
 */
export function isParameterRef(value: unknown): value is ParameterRef {
  if (typeof value !== "object" || value === null) return false;
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- runtime guard for untyped input
  return (value as ParameterRef).__type === "parameter";
}

// ============================================================
// Field Builder
// ============================================================

/**
 * A typed field builder for creating predicates.
 */
type FieldBuilder<T> =
  [T] extends [string] ? StringFieldBuilder<T>
  : [T] extends [number] ? NumberFieldBuilder<T>
  : [T] extends [boolean] ? BooleanFieldBuilder<T>
  : [T] extends [Date] ? DateFieldBuilder<T>
  : [T] extends [readonly (infer U)[]] ? ArrayFieldBuilder<U>
  : [T] extends [Record<string, unknown>] ? ObjectFieldBuilder<T>
  : BaseFieldBuilder<T>;

type EqualityOperand<T> = T | FieldRef<T> | ParameterRef;
type MembershipOperand<T> = readonly T[] | ParameterRef;

type NullFieldBuilder = Readonly<{
  isNull: () => Predicate;
  isNotNull: () => Predicate;
}>;

/**
 * Base field operations available on all types.
 */
type BaseFieldBuilder<T = unknown> = Readonly<{
  eq: (value: EqualityOperand<T>) => Predicate;
  neq: (value: EqualityOperand<T>) => Predicate;
  isNull: () => Predicate;
  isNotNull: () => Predicate;
  in: (values: MembershipOperand<T>) => Predicate;
  notIn: (values: MembershipOperand<T>) => Predicate;
}>;

/**
 * Options for the `$fulltext.matches()` predicate.
 *
 * Note the term glossary used throughout TypeGraph search:
 * - **`k`** (the positional argument on `.matches(query, k)`) is the
 *   per-predicate top-k cap applied inside the fulltext CTE.
 * - **`limit`** (on store-level `search.fulltext` / `search.hybrid` and
 *   on the query builder) is the final result count returned to the
 *   caller after any fusion.
 */
type MatchesOptions = Readonly<{
  /**
   * Parse mode for the query string. Default: "websearch".
   *
   * - "websearch": Google-style syntax (quoted phrases, `-excluded`, `OR`).
   *   Postgres: `websearch_to_tsquery`. SQLite: translated to FTS5 MATCH.
   * - "phrase": treats the whole query as a phrase.
   * - "plain": splits on whitespace and ANDs terms.
   * - "raw": dialect-native syntax passed through unchanged.
   */
  mode?: FulltextQueryMode;
  /**
   * Language override for query parsing. Default: the kind's declared
   * language when every kind in the alias shares one (a plan-time
   * constant, so PostgreSQL can serve the match from the GIN index);
   * mixed-language subclass aliases fall back to the per-row language
   * column.
   */
  language?: string;
  /** Minimum relevance score to include. */
  minScore?: number;
}>;

/**
 * String-specific field operations.
 *
 * Fulltext match is exposed on `n.$fulltext.matches(...)` at the node
 * level (gated by `searchable()` declarations in the schema), not on
 * individual string fields.
 */
type StringFieldBuilder<T extends string = string> = BaseFieldBuilder<T> &
  Readonly<{
    gt: (value: string | ParameterRef) => Predicate;
    gte: (value: string | ParameterRef) => Predicate;
    lt: (value: string | ParameterRef) => Predicate;
    lte: (value: string | ParameterRef) => Predicate;
    contains: (pattern: string | ParameterRef) => Predicate;
    startsWith: (pattern: string | ParameterRef) => Predicate;
    endsWith: (pattern: string | ParameterRef) => Predicate;
    like: (pattern: string | ParameterRef) => Predicate;
    ilike: (pattern: string | ParameterRef) => Predicate;
  }>;

/**
 * Number-specific field operations.
 */
type NumberFieldBuilder<T extends number = number> = BaseFieldBuilder<T> &
  Readonly<{
    gt: (value: number | ParameterRef) => Predicate;
    gte: (value: number | ParameterRef) => Predicate;
    lt: (value: number | ParameterRef) => Predicate;
    lte: (value: number | ParameterRef) => Predicate;
    between: (
      lower: number | ParameterRef,
      upper: number | ParameterRef,
    ) => Predicate;
  }>;

/**
 * Boolean-specific field operations.
 */
type BooleanFieldBuilder<T extends boolean = boolean> = BaseFieldBuilder<T>;

/**
 * Date-specific field operations.
 */
type DateFieldBuilder<T extends Date = Date> = BaseFieldBuilder<T> &
  Readonly<{
    gt: (value: Date | string | ParameterRef) => Predicate;
    gte: (value: Date | string | ParameterRef) => Predicate;
    lt: (value: Date | string | ParameterRef) => Predicate;
    lte: (value: Date | string | ParameterRef) => Predicate;
    between: (
      lower: Date | string | ParameterRef,
      upper: Date | string | ParameterRef,
    ) => Predicate;
  }>;

type ScalarValue = string | number | boolean | Date;

type ArrayPredicateOps<T> =
  [T] extends [ScalarValue] ?
    Readonly<{
      /** Check if array contains a specific value */
      contains: (value: T) => Predicate;
      /** Check if array contains all specified values */
      containsAll: (values: readonly T[]) => Predicate;
      /** Check if array contains any of the specified values (overlaps) */
      containsAny: (values: readonly T[]) => Predicate;
    }>
  : Readonly<Record<string, never>>;

/**
 * Array-specific field operations.
 */
type ArrayFieldBuilder<T = unknown> = NullFieldBuilder &
  Readonly<{
    /** Check if array is empty */
    isEmpty: () => Predicate;
    /** Check if array is not empty */
    isNotEmpty: () => Predicate;
    /** Check if array length equals a value */
    lengthEq: (length: number) => Predicate;
    /** Check if array length is greater than a value */
    lengthGt: (length: number) => Predicate;
    /** Check if array length is greater than or equal to a value */
    lengthGte: (length: number) => Predicate;
    /** Check if array length is less than a value */
    lengthLt: (length: number) => Predicate;
    /** Check if array length is less than or equal to a value */
    lengthLte: (length: number) => Predicate;
  }> &
  ArrayPredicateOps<T>;

/**
 * Object/JSON-specific field operations.
 */
type ResolvedPointerInput<T, Pointer> =
  Pointer extends string ? ResolveJsonPointer<T, Pointer>
  : Pointer extends readonly JsonPointerSegment[] ?
    ResolveJsonPointerSegments<T, Pointer>
  : unknown;

/**
 * Resolves the type of a nested key access on an object type.
 * Returns ObjectFieldBuilder for nested objects, FieldBuilder for scalars.
 */
type NestedFieldBuilder<T, K extends keyof T> =
  NonNullable<T[K]> extends Record<string, unknown> ?
    ObjectFieldBuilder<NonNullable<T[K]>>
  : FieldBuilder<T[K]>;

type ObjectFieldBuilder<
  T extends Record<string, unknown> = Record<string, unknown>,
> = NullFieldBuilder &
  Readonly<{
    /** Access a nested field by key for fluent chaining */
    get: <K extends keyof T & string>(key: K) => NestedFieldBuilder<T, K>;
    /** Check if object has a specific key at root level */
    hasKey: (key: string) => Predicate;
    /** Check if object has a nested path (JSON Pointer) */
    hasPath: <P extends JsonPointerInput<T>>(pointer: P) => Predicate;
    /** Check if value at path equals a value */
    pathEquals: <P extends JsonPointerInput<T>>(
      pointer: P,
      value: string | number | boolean | Date,
    ) => Predicate;
    /** Check if value at path (array) contains a value */
    pathContains: <P extends JsonPointerInput<T>>(
      pointer: P,
      value: string | number | boolean | Date,
    ) => Predicate;
    /** Check if value at path is null */
    pathIsNull: <P extends JsonPointerInput<T>>(pointer: P) => Predicate;
    /** Check if value at path is not null */
    pathIsNotNull: <P extends JsonPointerInput<T>>(pointer: P) => Predicate;
    /** Access a nested field to get a typed field builder */
    field: <P extends JsonPointerInput<T>>(
      pointer: P,
    ) => FieldBuilder<ResolvedPointerInput<T, P>>;
  }>;

/**
 * Options for the similarTo method.
 */
export type SimilarToOptions = Readonly<{
  /** Similarity metric to use. Default: "cosine" */
  metric?: VectorMetricType;
  /**
   * Minimum similarity score to include results.
   * For cosine: 0-1 where 1 is identical.
   * For L2: maximum distance to include.
   * For inner_product: minimum inner product value.
   */
  minScore?: number;
  /**
   * Opt into approximate retrieval: each declaring kind's candidates come
   * from the engine's native ANN structure (pgvector HNSW/IVFFlat scans,
   * vec0 `MATCH … k=`, libSQL `vector_top_k`) instead of an exact
   * distance scan. This is a SEMANTIC change — results are subject to the
   * index's recall, and predicates composed alongside constrain the ANN
   * candidate set (exact on pgvector/vec0; bounded by over-fetch on
   * libSQL). Default: exact.
   *
   * Two states cannot serve it, and they are handled differently on purpose:
   *
   * - **A slot declared `indexType: "none"`** has no ANN structure, so the
   *   opt-in compiles to the exact scan and results are unchanged. A
   *   DECLARED degradation: the query is still exactly what was asked for,
   *   only faster or slower.
   * - **A `metric` override that differs from the field's declared metric**
   *   is REFUSED with a `ConfigurationError` naming both metrics. An ANN
   *   structure retrieves only under the metric it was built for, so the two
   *   options state something that cannot both hold — and serving the exact
   *   scan instead would silently drop one of them. Omit `metric` (or pass
   *   the declared one) to keep approximate retrieval, or drop `approximate`
   *   to scan exactly under the overriding metric.
   */
  approximate?: boolean;
}>;

/**
 * Embedding-specific field operations for vector similarity search.
 */
type EmbeddingFieldBuilder = NullFieldBuilder &
  Readonly<{
    /**
     * Find nodes with similar embeddings.
     * Returns results ordered by similarity (most similar first).
     *
     * @param queryEmbedding - The embedding vector to compare against
     * @param k - Maximum number of results to return (top-k)
     * @param options - Optional configuration (metric, minScore)
     *
     * @example
     * ```typescript
     * store.query()
     *   .from("Document", "d")
     *   .whereNode("d", (d) =>
     *     d.embedding.similarTo(queryVector, 10, { metric: "cosine" })
     *   )
     *   .select((ctx) => ({ doc: ctx.d }))
     *   .execute()
     * ```
     */
    similarTo: (
      queryEmbedding: readonly number[],
      k: number,
      options?: SimilarToOptions,
    ) => Predicate;
  }>;

// ============================================================
// Field Builder Factory
// ============================================================

/**
 * Creates a field reference.
 */
type FieldRefOptions = Readonly<{
  jsonPointer?: JsonPointer | undefined;
  valueType?: ValueType | undefined;
  elementType?: ValueType | undefined;
  nullable?: boolean | undefined;
}>;

export function fieldRef<Value = unknown>(
  alias: string,
  path: readonly string[],
  options?: FieldRefOptions,
): FieldRef<Value> {
  return {
    __type: "field_ref",
    alias,
    path,
    ...(options?.jsonPointer !== undefined && {
      jsonPointer: options.jsonPointer,
    }),
    ...(options?.valueType !== undefined && { valueType: options.valueType }),
    ...(options?.elementType !== undefined && {
      elementType: options.elementType,
    }),
    ...(options?.nullable !== undefined && { nullable: options.nullable }),
  };
}

function isFieldRef(value: unknown): value is FieldRef {
  return (
    typeof value === "object" &&
    value !== null &&
    (value as Readonly<{ __type?: unknown }>).__type === "field_ref"
  );
}

function assertCompatibleFieldReferences(
  left: FieldRef,
  right: FieldRef,
): void {
  const leftType = left.valueType;
  const rightType = right.valueType;
  if (
    leftType !== undefined &&
    leftType !== "unknown" &&
    rightType !== undefined &&
    rightType !== "unknown" &&
    leftType !== rightType
  ) {
    throw new UnsupportedPredicateError(
      `Cannot compare ${leftType} field "${left.alias}.${left.path.join(".")}" with ${rightType} field "${right.alias}.${right.path.join(".")}".`,
      { leftType, rightType },
    );
  }
}

function assertLiteralMatchesField(
  field: FieldRef,
  value: unknown,
  allowDateString = false,
): void {
  const valueType = field.valueType;
  if (valueType === undefined || valueType === "unknown") return;

  const matches =
    (valueType === "string" && typeof value === "string") ||
    (valueType === "number" && typeof value === "number") ||
    (valueType === "boolean" && typeof value === "boolean") ||
    (valueType === "date" &&
      (value instanceof Date ||
        (allowDateString && typeof value === "string")));
  if (matches) return;

  throw new UnsupportedPredicateError(
    `Expected a ${valueType} literal for field "${field.alias}.${field.path.join(".")}", got ${value instanceof Date ? "date" : typeof value}.`,
    { expectedValueType: valueType, receivedValueType: typeof value },
  );
}

function assertComparisonSupported(field: FieldRef): void {
  const valueType = field.valueType;
  if (
    valueType === "array" ||
    valueType === "object" ||
    valueType === "embedding"
  ) {
    throw new UnsupportedPredicateError(
      `Comparison predicates are not supported for ${valueType} field "${field.alias}.${field.path.join(".")}".`,
      { valueType },
    );
  }
}

function coerceLiteralValue(value: unknown): string | number | boolean | Date {
  if (value instanceof Date) {
    return value;
  }
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value;
  }
  throw new Error(`Unsupported literal value type: ${typeof value}`);
}

function inferLiteralValueType(value: string | number | boolean): ValueType {
  if (typeof value === "string") {
    return "string";
  }
  if (typeof value === "number") {
    return "number";
  }
  return "boolean";
}

/**
 * Creates a literal value.
 */
function literal(value: string | number | boolean | Date): LiteralValue {
  if (value instanceof Date) {
    return { __type: "literal", value: value.toISOString(), valueType: "date" };
  }
  const valueType = inferLiteralValueType(value);
  return {
    __type: "literal",
    value,
    valueType,
  };
}

/**
 * Creates a comparison predicate.
 */
function comparison(
  op: ComparisonOp,
  field: FieldRef,
  value: unknown,
): Predicate {
  assertComparisonSupported(field);
  if (isParameterRef(value)) {
    const expr: ComparisonPredicate = {
      __type: "comparison",
      op,
      left: field,
      right: value,
    };
    return predicate(expr);
  }
  if (isFieldRef(value)) {
    assertCompatibleFieldReferences(field, value);
    return predicate({
      __type: "comparison",
      op,
      left: field,
      right: value,
    } satisfies ComparisonPredicate);
  }
  assertLiteralMatchesField(field, value, op !== "eq" && op !== "neq");
  const coercedValue = coerceLiteralValue(value);
  const expr: ComparisonPredicate = {
    __type: "comparison",
    op,
    left: field,
    right: literal(coercedValue),
  };
  return predicate(expr);
}

/**
 * Creates an IN comparison predicate.
 *
 * The whole list may be a single `param()` — bound at
 * `.prepare().execute({ ids: [...] })` and compiled to one placeholder, so
 * varying list lengths reuse one cached statement. A `param()` sitting among
 * literal *elements* has no such representation (a placeholder cannot be one
 * item of a list the compiler already inlined), so it is rejected rather than
 * silently bound as a literal.
 */
function inComparison(
  op: "in" | "notIn",
  field: FieldRef,
  values: readonly unknown[] | ParameterRef,
): Predicate {
  assertComparisonSupported(field);
  if (isParameterRef(values)) {
    return predicate({
      __type: "comparison",
      op,
      left: field,
      right: values,
    } satisfies ComparisonPredicate);
  }
  const literals = values.map((value) => {
    if (isParameterRef(value)) {
      throw new UnsupportedPredicateError(
        `param("${value.name}") is not supported as an element of the ${op}() list`,
        { parameterName: value.name },
        {
          suggestion: `Bind the whole list instead: .${op}(param("${value.name}")) and execute({ ${value.name}: [...] }).`,
        },
      );
    }
    assertLiteralMatchesField(field, value);
    return literal(coerceLiteralValue(value));
  });
  const expr: ComparisonPredicate = {
    __type: "comparison",
    op,
    left: field,
    right: literals,
  };
  return predicate(expr);
}

/**
 * Creates a string operation predicate.
 */
function stringOp(
  op: StringOp,
  field: FieldRef,
  pattern: string | ParameterRef,
): Predicate {
  if (isParameterRef(pattern)) {
    const expr: StringPredicate = {
      __type: "string_op",
      op,
      field,
      pattern,
    };
    return predicate(expr);
  }
  const expr: StringPredicate = {
    __type: "string_op",
    op,
    field,
    pattern,
  };
  return predicate(expr);
}

/**
 * Creates a null check predicate.
 */
function nullCheck(op: NullCheckOp, field: FieldRef): Predicate {
  const expr: NullPredicate = {
    __type: "null_check",
    op,
    field,
  };
  return predicate(expr);
}

function nullFieldBuilder(field: FieldRef): NullFieldBuilder {
  return {
    isNull: () => nullCheck("isNull", field),
    isNotNull: () => nullCheck("isNotNull", field),
  };
}

/**
 * Creates a between predicate.
 */
function between(
  field: FieldRef,
  lower: string | number | boolean | Date | ParameterRef,
  upper: string | number | boolean | Date | ParameterRef,
): Predicate {
  const expr: BetweenPredicate = {
    __type: "between",
    field,
    lower: isParameterRef(lower) ? lower : literal(lower),
    upper: isParameterRef(upper) ? upper : literal(upper),
  };
  return predicate(expr);
}

/**
 * Creates a base field builder with common operations.
 */
function baseFieldBuilder<T = unknown>(field: FieldRef): BaseFieldBuilder<T> {
  return {
    eq: (value) => comparison("eq", field, value),
    neq: (value) => comparison("neq", field, value),
    isNull: () => nullCheck("isNull", field),
    isNotNull: () => nullCheck("isNotNull", field),
    in: (values) => inComparison("in", field, values),
    notIn: (values) => inComparison("notIn", field, values),
  };
}

function unsupportedComparisonFieldBuilder(field: FieldRef): BaseFieldBuilder {
  return {
    eq: (value) => comparison("eq", field, value),
    neq: (value) => comparison("neq", field, value),
    in: (values) => inComparison("in", field, values),
    notIn: (values) => inComparison("notIn", field, values),
    ...nullFieldBuilder(field),
  };
}

/**
 * Creates a string field builder.
 *
 * Fulltext match is not a string-field operation — use
 * `n.$fulltext.matches(...)` at the node level. `searchable()`
 * declarations determine which node kinds expose `$fulltext`.
 */
export function stringField(field: FieldRef): StringFieldBuilder {
  return {
    ...baseFieldBuilder(field),
    gt: (value) => comparison("gt", field, value),
    gte: (value) => comparison("gte", field, value),
    lt: (value) => comparison("lt", field, value),
    lte: (value) => comparison("lte", field, value),
    contains: (pattern) => stringOp("contains", field, pattern),
    startsWith: (pattern) => stringOp("startsWith", field, pattern),
    endsWith: (pattern) => stringOp("endsWith", field, pattern),
    like: (pattern) => stringOp("like", field, pattern),
    ilike: (pattern) => stringOp("ilike", field, pattern),
  };
}

/**
 * Default top-k for `$fulltext.matches()` when the caller does not pass
 * one. Large enough to leave headroom for downstream `.and()` filters,
 * small enough to keep latency reasonable on any dialect.
 */
const DEFAULT_FULLTEXT_MATCH_K = 50;

/**
 * Node-level fulltext accessor. Always present on the `NodeAccessor`
 * type — a runtime check throws a clear error if the node kind has no
 * `searchable()` fields. `k` is the top-k cap applied inside the
 * fulltext CTE; defaults to `DEFAULT_FULLTEXT_MATCH_K`. Use a larger
 * value when feeding the result into RRF (`.fuseWith()` /
 * `store.search.hybrid()`).
 */
export type FulltextAccessor = Readonly<{
  matches: (query: string, k?: number, options?: MatchesOptions) => Predicate;
}>;

/**
 * Creates a node-level fulltext accessor. The runtime check throws for
 * aliases whose node kind has no `searchable()` content, catching the
 * case where the field tag was dropped (e.g. by a schema refactor)
 * after the query was written.
 */
export function createFulltextAccessor(
  alias: string,
  hasSearchableField: () => boolean,
): FulltextAccessor {
  return {
    matches: (query, k, options) => {
      if (!hasSearchableField()) {
        throw new UnsupportedPredicateError(
          `Cannot call .$fulltext.matches() on alias "${alias}" — its ` +
            `node kind has no fields declared with searchable(). Add at ` +
            `least one: \`title: searchable({ language: "english" })\`.`,
        );
      }
      return fulltextMatch(
        fieldRef(alias, ["$fulltext"]),
        query,
        k ?? DEFAULT_FULLTEXT_MATCH_K,
        options,
      );
    },
  };
}

/**
 * Creates a fulltext match predicate.
 */
function fulltextMatch(
  field: FieldRef,
  query: string,
  limit: number,
  options?: MatchesOptions,
): Predicate {
  if (!Number.isFinite(limit) || limit <= 0) {
    throw new UnsupportedPredicateError(
      `matches() limit must be a positive finite number, got ${String(limit)}`,
    );
  }
  const expr: FulltextMatchPredicate = {
    __type: "fulltext_match",
    field,
    query,
    mode: options?.mode ?? "websearch",
    limit,
    ...(options?.language === undefined ? {} : { language: options.language }),
    ...(options?.minScore === undefined ? {} : { minScore: options.minScore }),
  };
  return predicate(expr);
}

/**
 * Creates a number field builder.
 */
export function numberField(field: FieldRef): NumberFieldBuilder {
  return {
    ...baseFieldBuilder(field),
    gt: (value) => comparison("gt", field, value),
    gte: (value) => comparison("gte", field, value),
    lt: (value) => comparison("lt", field, value),
    lte: (value) => comparison("lte", field, value),
    between: (lower, upper) => between(field, lower, upper),
  };
}

/**
 * Creates a date field builder.
 */
export function dateField(field: FieldRef): DateFieldBuilder {
  return {
    ...baseFieldBuilder(field),
    gt: (value) => comparison("gt", field, value),
    gte: (value) => comparison("gte", field, value),
    lt: (value) => comparison("lt", field, value),
    lte: (value) => comparison("lte", field, value),
    between: (lower, upper) => between(field, lower, upper),
  };
}

/**
 * Creates a base field builder (for booleans, enums, and unknown types).
 * Only provides the fundamental operations: eq, neq, isNull, isNotNull, in, notIn.
 */
export function baseField<T = unknown>(
  field: FieldRef<T>,
): BaseFieldBuilder<T> {
  return baseFieldBuilder(field);
}

/**
 * Creates an array operation predicate.
 */
function arrayOp(
  op: ArrayOp,
  field: FieldRef,
  values?: readonly unknown[],
  length?: number,
): Predicate {
  const expr: ArrayPredicate = {
    __type: "array_op",
    op,
    field,
    ...(values !== undefined && {
      values: values.map((value) => literal(coerceLiteralValue(value))),
    }),
    ...(length !== undefined && { length }),
  };
  return predicate(expr);
}

/**
 * Creates an array field builder.
 */
export function arrayField<T = unknown>(field: FieldRef): ArrayFieldBuilder<T> {
  // The type assertion is needed because ArrayFieldBuilder conditionally includes
  // contains/containsAll/containsAny based on whether T extends ScalarValue.
  // At runtime we always provide them - they just won't type-check if T isn't scalar.
  return {
    ...nullFieldBuilder(field),
    contains: (value: T) => arrayOp("contains", field, [value]),
    containsAll: (values: readonly T[]) =>
      arrayOp("containsAll", field, values),
    containsAny: (values: readonly T[]) =>
      arrayOp("containsAny", field, values),
    isEmpty: () => arrayOp("isEmpty", field),
    isNotEmpty: () => arrayOp("isNotEmpty", field),
    lengthEq: (length: number) => arrayOp("lengthEq", field, undefined, length),
    lengthGt: (length: number) => arrayOp("lengthGt", field, undefined, length),
    lengthGte: (length: number) =>
      arrayOp("lengthGte", field, undefined, length),
    lengthLt: (length: number) => arrayOp("lengthLt", field, undefined, length),
    lengthLte: (length: number) =>
      arrayOp("lengthLte", field, undefined, length),
  } as unknown as ArrayFieldBuilder<T>;
}

/**
 * Creates a vector similarity predicate.
 */
function vectorSimilarity(
  field: FieldRef,
  queryEmbedding: readonly number[],
  limit: number,
  options?: SimilarToOptions,
): Predicate {
  const expr: VectorSimilarityPredicate = {
    __type: "vector_similarity",
    field,
    queryEmbedding,
    // Leave metric unset when the caller didn't specify one, so the compiler
    // resolves the field's declared metric per kind. Defaulting to "cosine"
    // here would mis-rank l2/inner_product fields and bypass their ANN index.
    ...(options?.metric !== undefined && { metric: options.metric }),
    limit,
    ...(options?.minScore !== undefined && { minScore: options.minScore }),
    ...(options?.approximate !== undefined && {
      approximate: options.approximate,
    }),
  };
  return predicate(expr);
}

/**
 * Creates an embedding field builder for vector similarity search.
 */
export function embeddingField(field: FieldRef): EmbeddingFieldBuilder {
  return {
    ...nullFieldBuilder(field),
    similarTo: (queryEmbedding, k, options) =>
      vectorSimilarity(field, queryEmbedding, k, options),
  };
}

export function buildFieldBuilderForTypeInfo(
  field: FieldRef,
  typeInfo: FieldTypeInfo | undefined,
): BaseFieldBuilder {
  if (!typeInfo) {
    return baseField(field);
  }

  switch (typeInfo.valueType) {
    case "string": {
      return stringField(field) as unknown as BaseFieldBuilder;
    }
    case "number": {
      return numberField(field) as unknown as BaseFieldBuilder;
    }
    case "boolean": {
      return baseField(field);
    }
    case "date": {
      return dateField(field) as unknown as BaseFieldBuilder;
    }
    case "array": {
      return {
        ...arrayField(field),
        ...unsupportedComparisonFieldBuilder(field),
      };
    }
    case "object": {
      return {
        ...objectField(field, { typeInfo }),
        ...unsupportedComparisonFieldBuilder(field),
      };
    }
    case "embedding": {
      return {
        ...embeddingField(field),
        ...unsupportedComparisonFieldBuilder(field),
      };
    }
    case "unknown": {
      return baseField(field);
    }
  }
}

function resolvePointerTypeInfo(
  typeInfo: FieldTypeInfo | undefined,
  pointer: JsonPointer,
): FieldTypeInfo | undefined {
  return resolveFieldTypeInfoAtJsonPointer(typeInfo, pointer);
}

/**
 * Creates an object operation predicate.
 */
type ObjectPredicateOptions = Readonly<{
  valueType?: ValueType;
  elementType?: ValueType;
}>;

function objectOp(
  op: ObjectOp,
  field: FieldRef,
  pointer: JsonPointer,
  value?: unknown,
  options?: ObjectPredicateOptions,
): Predicate {
  const expr: ObjectPredicate = {
    __type: "object_op",
    op,
    field,
    pointer,
    ...(value !== undefined && {
      value: literal(coerceLiteralValue(value)),
    }),
    ...(options?.valueType !== undefined && { valueType: options.valueType }),
    ...(options?.elementType !== undefined && {
      elementType: options.elementType,
    }),
  };
  return predicate(expr);
}

/**
 * Creates an object/JSON field builder.
 */
type ObjectFieldOptions = Readonly<{
  typeInfo?: FieldTypeInfo;
}>;

export function objectField<
  T extends Record<string, unknown> = Record<string, unknown>,
>(field: FieldRef, options?: ObjectFieldOptions): ObjectFieldBuilder<T> {
  const basePointer = field.jsonPointer;

  return {
    ...nullFieldBuilder(field),
    get: <K extends keyof T & string>(key: K) => {
      const pointer = jsonPointer([key]);
      const nestedPointer = joinJsonPointers(basePointer, pointer);
      const resolved = resolvePointerTypeInfo(options?.typeInfo, pointer);
      const nestedRef = fieldRef(field.alias, field.path, {
        jsonPointer: nestedPointer,
        ...(resolved?.valueType !== undefined && {
          valueType: resolved.valueType,
        }),
        ...(resolved?.elementType !== undefined && {
          elementType: resolved.elementType,
        }),
      });
      return buildFieldBuilderForTypeInfo(nestedRef, resolved) as ReturnType<
        ObjectFieldBuilder<T>["get"]
      >;
    },
    hasKey: (key) => {
      const pointer = jsonPointer([key]);
      resolvePointerTypeInfo(options?.typeInfo, pointer);
      return objectOp("hasKey", field, pointer);
    },
    hasPath: <P extends JsonPointerInput<T>>(pointer: P) => {
      const normalized = normalizeJsonPointer(pointer as JsonPointerInput<T>);
      resolvePointerTypeInfo(options?.typeInfo, normalized);
      return objectOp("hasPath", field, normalized);
    },
    pathEquals: <P extends JsonPointerInput<T>>(
      pointer: P,
      value: string | number | boolean | Date,
    ) => {
      const normalized = normalizeJsonPointer(pointer as JsonPointerInput<T>);
      const resolved = resolvePointerTypeInfo(options?.typeInfo, normalized);
      if (
        resolved &&
        (resolved.valueType === "array" || resolved.valueType === "object")
      ) {
        throw new Error("pathEquals is only supported for scalar JSON values");
      }
      const predicateOptions: ObjectPredicateOptions | undefined =
        resolved?.valueType === undefined ?
          undefined
        : { valueType: resolved.valueType };
      return objectOp("pathEquals", field, normalized, value, predicateOptions);
    },
    pathContains: <P extends JsonPointerInput<T>>(
      pointer: P,
      value: string | number | boolean | Date,
    ) => {
      const normalized = normalizeJsonPointer(pointer as JsonPointerInput<T>);
      const resolved = resolvePointerTypeInfo(options?.typeInfo, normalized);
      if (resolved && resolved.valueType !== "array") {
        throw new Error("pathContains is only supported for JSON array values");
      }
      const predicateOptions: ObjectPredicateOptions | undefined =
        resolved?.elementType === undefined ?
          undefined
        : { elementType: resolved.elementType };
      return objectOp(
        "pathContains",
        field,
        normalized,
        value,
        predicateOptions,
      );
    },
    pathIsNull: <P extends JsonPointerInput<T>>(pointer: P) => {
      const normalized = normalizeJsonPointer(pointer as JsonPointerInput<T>);
      const resolved = resolvePointerTypeInfo(options?.typeInfo, normalized);
      const predicateOptions: ObjectPredicateOptions | undefined =
        resolved?.valueType === undefined ?
          undefined
        : { valueType: resolved.valueType };
      return objectOp(
        "pathIsNull",
        field,
        normalized,
        undefined,
        predicateOptions,
      );
    },
    pathIsNotNull: <P extends JsonPointerInput<T>>(pointer: P) => {
      const normalized = normalizeJsonPointer(pointer as JsonPointerInput<T>);
      const resolved = resolvePointerTypeInfo(options?.typeInfo, normalized);
      const predicateOptions: ObjectPredicateOptions | undefined =
        resolved?.valueType === undefined ?
          undefined
        : { valueType: resolved.valueType };
      return objectOp(
        "pathIsNotNull",
        field,
        normalized,
        undefined,
        predicateOptions,
      );
    },
    field: <P extends JsonPointerInput<T>>(pointer: P) => {
      const normalized = normalizeJsonPointer(pointer as JsonPointerInput<T>);
      const nestedPointer = joinJsonPointers(basePointer, normalized);
      const resolved = resolvePointerTypeInfo(options?.typeInfo, normalized);
      const nestedRef = fieldRef(field.alias, field.path, {
        jsonPointer: nestedPointer,
        ...(resolved?.valueType !== undefined && {
          valueType: resolved.valueType,
        }),
        ...(resolved?.elementType !== undefined && {
          elementType: resolved.elementType,
        }),
      });
      return buildFieldBuilderForTypeInfo(nestedRef, resolved) as FieldBuilder<
        ResolvedPointerInput<T, P>
      >;
    },
  };
}

// ============================================================
// Subquery Predicates
// ============================================================

/**
 * Creates an EXISTS subquery predicate.
 * Returns true if the subquery returns at least one row.
 *
 * @param subquery - The subquery AST to check for existence
 *
 * @example
 * ```typescript
 * // Find persons who have at least one order
 * query
 *   .from("Person", "p")
 *   .whereNode("p", () =>
 *     exists(
 *       query.from("Order", "o")
 *         .whereNode("o", (o) => o.customerId.eq(field("p.id")))
 *         .select((ctx) => ({ id: ctx.o.id }))
 *         .toAst()
 *     )
 *   )
 * ```
 */
export function exists(subquery: QueryAst): Predicate {
  const expr: ExistsSubquery = {
    __type: "exists",
    subquery,
    negated: false,
  };
  return predicate(expr);
}

/**
 * Creates a NOT EXISTS subquery predicate.
 * Returns true if the subquery returns no rows.
 *
 * @param subquery - The subquery AST to check for non-existence
 */
export function notExists(subquery: QueryAst): Predicate {
  const expr: ExistsSubquery = {
    __type: "exists",
    subquery,
    negated: true,
  };
  return predicate(expr);
}

/**
 * Creates an IN subquery predicate.
 * Returns true if the field value is in the subquery results.
 *
 * @param field - The field to check
 * @param subquery - The subquery AST that returns a single column
 *
 * @example
 * ```typescript
 * // Find persons whose ID is in the VIP list
 * query
 *   .from("Person", "p")
 *   .where(() =>
 *     inSubquery(
 *       fieldRef("p", ["id"]),
 *       query.from("VIPMember", "v")
 *         .aggregate({
 *           id: fieldRef("v", ["props", "personId"], { valueType: "string" }),
 *         })
 *         .toAst()
 *     )
 *   )
 * ```
 */
export function inSubquery(field: FieldRef, subquery: QueryAst): Predicate {
  assertSingleColumnSubquery(subquery);
  assertCompatibleSubqueryValueTypes(field, subquery);
  const expr: InSubquery = {
    __type: "in_subquery",
    field,
    subquery,
    negated: false,
  };
  return predicate(expr);
}

/**
 * Creates a NOT IN subquery predicate.
 * Returns true if the field value is not in the subquery results.
 *
 * @param field - The field to check
 * @param subquery - The subquery AST that returns a single column
 */
export function notInSubquery(field: FieldRef, subquery: QueryAst): Predicate {
  assertSingleColumnSubquery(subquery);
  assertCompatibleSubqueryValueTypes(field, subquery);
  const expr: InSubquery = {
    __type: "in_subquery",
    field,
    subquery,
    negated: true,
  };
  return predicate(expr);
}

/**
 * Ensures IN/NOT IN subqueries project exactly one column.
 */
function assertSingleColumnSubquery(subquery: QueryAst): void {
  const columnCount = getSubqueryColumnCount(subquery);
  if (columnCount === 1) return;

  throw new UnsupportedPredicateError(
    `IN/NOT IN subquery must project exactly 1 column, but got ${columnCount}`,
    { subqueryColumnCount: columnCount },
    {
      suggestion:
        "Use aggregate() with exactly one projected field for scalar IN/NOT IN subqueries.",
    },
  );
}

/**
 * Ensures IN/NOT IN subquery and field value types are compatible when known.
 */
function assertCompatibleSubqueryValueTypes(
  field: FieldRef,
  subquery: QueryAst,
): void {
  const fieldValueType = field.valueType;
  const subqueryValueType = getSingleSubqueryColumnValueType(subquery);
  const resolvedValueType = fieldValueType ?? subqueryValueType;

  if (isUnsupportedInSubqueryValueType(resolvedValueType)) {
    throw new UnsupportedPredicateError(
      `IN/NOT IN subquery does not support ${String(resolvedValueType)} values`,
      { valueType: resolvedValueType },
      {
        suggestion:
          "Use scalar fields (string/number/boolean/date) in IN/NOT IN predicates.",
      },
    );
  }

  if (isInSubqueryTypeCompatible(fieldValueType, subqueryValueType)) {
    return;
  }

  throw new UnsupportedPredicateError(
    `IN/NOT IN type mismatch: field type "${String(fieldValueType)}" does not match subquery column type "${String(subqueryValueType)}"`,
    {
      fieldValueType,
      subqueryValueType,
    },
  );
}
